import {
  CheckBox,
  DisplayItem,
  DropDown,
  FormHookResult,
  helpers,
  NetworkIndicator,
  RadioButtonGroup,
  SearchableDropDown,
  TextField,
} from 'design-system';
import React, { ReactElement } from 'react';
import { MoneyField } from 'src/components/inputs';
import validationNames from 'src/functions/validationNames';

export type FormFieldProps = {
  key?: string;
  inputName: string;
  label: string;
  getInputProps: FormHookResult;
  inputId: string;
  helperText?: string;
  isDisabled?: boolean;
  showError?: boolean;
  isLoading?: boolean;
  errorMessage?: string;
  shouldUpdate?: (newVal: string) => boolean;
  placeholder?: string;
  personType?: string;
  onBlur?: () => void;
};

// Type For CheckBox Field
type CheckBoxFieldProps = {
  checkTitle?: React.ReactNode;
  checkName: string;
  getInputProps: FormHookResult;
  disabled?: boolean;
  defaultValue?: boolean;
  inputId?: string;
  onChange?: (val: boolean) => void;
  dataSource?: FormHookResult<boolean>;
  style?: React.CSSProperties;
};

export interface DropDownProps extends FormFieldProps {
  dropdownList: DisplayItem[];
}

export function defineDecoration(isDisabled: boolean): React.ReactNode {
  return isDisabled ? <div /> : undefined;
}

/**
 * Wrapper renderers of form fields
 * This will help reduce the verbosity/lines of code in our components
 * and stay within Sonarcloud's 200 lines of code limit
 */
export function makeTextField({
  key,
  inputName,
  label,
  getInputProps,
  inputId,
  isDisabled,
  helperText,
  shouldUpdate,
  placeholder,
  personType,
  isLoading,
  onBlur,
}: FormFieldProps): React.ReactNode {
  const { value, onChange, error, errorText } = getInputProps(inputName);
  return (
    <TextField
      key={key}
      inputId={inputId}
      inputName={inputName}
      inputLabel={label}
      defaultValue={value as string}
      onChange={(val: string) => {
        const newValue = validationNames(val, inputName, personType);
        onChange(newValue);
      }}
      loading={isLoading}
      inputError={error}
      helperText={helperText}
      inputErrorMessage={errorText}
      isDisabled={isDisabled}
      shouldUpdate={shouldUpdate}
      customDecoration={defineDecoration(Boolean(isDisabled))}
      placeholder={placeholder}
      customBlur={onBlur}
    />
  );
}

export function makeTextIdField({
  onBlur,
  isLoading,
  personType,
  placeholder,
  shouldUpdate,
  helperText,
  isDisabled,
  inputId,
  getInputProps,
  label,
  inputName,
  key,
}: FormFieldProps): React.ReactNode {
  const { value, onChange, error, errorText } = getInputProps(inputName);
  return (
    <TextField
      inputId={inputId}
      inputName={inputName}
      key={key}
      defaultValue={value as string}
      inputLabel={label}
      loading={isLoading}
      onChange={(val: string) => {
        const newValue = validationNames(val, inputName, personType);
        onChange(newValue.toUpperCase());
      }}
      helperText={helperText}
      inputError={error}
      isDisabled={isDisabled}
      shouldUpdate={shouldUpdate}
      customDecoration={defineDecoration(Boolean(isDisabled))}
      placeholder={placeholder}
      customBlur={onBlur}
      inputErrorMessage={errorText}
    />
  );
}

export function makeCheckBoxField({
  checkTitle,
  checkName,
  getInputProps,
  disabled,
  inputId,
  dataSource,
  onChange,
  style,
}: CheckBoxFieldProps): ReactElement {
  const { value } = getInputProps(checkName);

  return (
    <CheckBox
      style={style}
      inputId={inputId}
      checkTitle={checkTitle}
      checkName={checkName}
      disabled={disabled}
      defaultValue={value as boolean}
      onChange={onChange}
      dataSource={dataSource}
    />
  );
}

export function makePercentageField({
  inputName,
  label,
  getInputProps,
  inputId,
  isDisabled,
  helperText,
  showError,
  errorMessage,
}: FormFieldProps): React.ReactNode {
  const { value, onChange, error, errorText } = getInputProps(inputName);
  return (
    <TextField
      inputId={inputId}
      inputName={inputName}
      inputLabel={label}
      defaultValue={value as string}
      onChange={onChange}
      inputError={error || showError}
      helperText={helperText}
      inputErrorMessage={errorText || errorMessage}
      isDisabled={isDisabled}
      maskOptions={{ numeral: true }}
      type="number"
      customDecoration="%"
    />
  );
}

const dateMaskData = {
  date: true,
  delimiter: '/',
  datePattern: ['d', 'm', 'Y'],
};

export function makeDateField({
  inputName,
  label,
  getInputProps,
  inputId,
  isDisabled,
  showError,
  isLoading,
}: FormFieldProps): React.ReactNode {
  const { value, onChange, error, errorText } = getInputProps(inputName);
  return (
    <TextField
      inputId={inputId}
      inputName={inputName}
      data-testid={inputName}
      inputLabel={label}
      defaultValue={value as string}
      onChange={onChange}
      isDisabled={isDisabled}
      loading={isLoading}
      inputError={showError || error}
      inputErrorMessage={errorText}
      maskOptions={dateMaskData}
      customDecoration={defineDecoration(Boolean(isDisabled))}
    />
  );
}

export function makeDropDown({
  inputName,
  label,
  getInputProps,
  inputId,
  dropdownList,
  isDisabled,
}: DropDownProps): React.ReactNode {
  const { value, onChange, error, errorText } = getInputProps(inputName);
  return (
    <DropDown
      inputId={inputId}
      inputName={inputName}
      inputLabel={label}
      defaultValue={value as string}
      options={dropdownList}
      onChange={(newVal): void => {
        onChange(newVal.Value);
      }}
      isDisabled={isDisabled}
      inputError={error}
      inputErrorMessage={errorText}
    />
  );
}

interface SearchableDropDownProps extends DropDownProps {
  requesting: boolean;
  onUpdate?: (value: string) => void;
}

export function makeSearchableDropDown({
  inputName,
  label,
  getInputProps,
  dropdownList = [],
  requesting,
  inputId,
  isDisabled,
  onUpdate,
}: SearchableDropDownProps): ReactElement {
  const { value, onChange, error, errorText } = getInputProps(inputName);
  if (requesting) {
    return (
      <NetworkIndicator
        data-testid={`${inputName}-loading`}
        requesting
        message="Cargando..."
        variant="horizontal"
      />
    );
  }
  return (
    <SearchableDropDown
      inputId={inputId}
      inputName={inputName}
      inputLabel={label}
      defaultValue={value as string}
      onChange={(newVal: DisplayItem): void => {
        onChange(newVal.Value);
        if (onUpdate) {
          onUpdate(newVal.Value);
        }
      }}
      inputError={error}
      inputErrorMessage={errorText}
      options={
        // Check if the inputId is 'swi-general-data-otherNationality' to add an empty option
        inputId === 'swi-general-data-otherNationality'
          ? [
              { Text: '', Value: '' },
              { Text: 'NO POSEO', Value: 'no_poseo' },
              ...dropdownList,
            ]
          : dropdownList
      }
      isDisabled={isDisabled}
    />
  );
}

type GridSizes = 3 | 4 | 6 | 12;
interface RadioGroupSelectorProps extends DropDownProps {
  gridSize?: GridSizes;
}

export function makeRadioGroupSelector({
  inputName,
  label,
  inputId,
  dropdownList,
  getInputProps,
  gridSize,
  isDisabled,
}: RadioGroupSelectorProps): JSX.Element {
  const { value, onChange, errorText } = getInputProps(inputName);
  return (
    <RadioButtonGroup
      inputId={inputId}
      data-testid={inputName}
      formTitle={label}
      radioData={dropdownList}
      defaultValue={value as string}
      radioGroupName={inputName}
      gridSize={gridSize}
      errorMessage={errorText}
      onChange={(newVal): void => {
        onChange(newVal.Value);
      }}
      disabled={isDisabled}
    />
  );
}

interface MoneyFieldProps extends FormFieldProps {
  currencyInputName: string;
}

export function makeMoneyField({
  inputName,
  currencyInputName,
  label,
  getInputProps,
  inputId,
  isDisabled,
}: MoneyFieldProps): JSX.Element {
  const { value, onChange, errorText, error } = getInputProps(inputName);
  return (
    <MoneyField
      inputId={inputId}
      inputName={inputName}
      inputLabel={label}
      defaultValue={value as number}
      inputError={error}
      isDisabled={isDisabled}
      inputErrorMessage={errorText}
      onChange={onChange}
      currencySelected={getInputProps(currencyInputName).value as string}
    />
  );
}

interface DefaultMoneyFieldProps extends FormFieldProps {
  currencyType: string;
  defaultValue: number;
}

export function makeDefaultMoneyField({
  inputName,
  currencyType,
  label,
  getInputProps,
  inputId,
  isDisabled,
  defaultValue,
}: DefaultMoneyFieldProps): JSX.Element {
  const { onChange, errorText, error } = getInputProps(inputName);
  return (
    <MoneyField
      inputId={inputId}
      inputName={inputName}
      inputLabel={label}
      defaultValue={defaultValue}
      inputError={error}
      isDisabled={isDisabled}
      inputErrorMessage={errorText}
      onChange={onChange}
      currencySelected={currencyType}
    />
  );
}

interface CreditCardFieldProps extends FormFieldProps {
  cardTypeInputName: string;
}

export function makeCreditCardField({
  inputName,
  cardTypeInputName,
  label,
  getInputProps,
  inputId,
  isDisabled,
  helperText,
}: CreditCardFieldProps): React.ReactNode {
  const { value: cardType, onChange: onChangeType } =
    getInputProps(cardTypeInputName);
  const { value, onChange, error, errorText } = getInputProps(inputName);
  const creditCardIcon = helpers.cardTypeIcon(cardType as string);
  const handleChange = (newVal: string): void => {
    onChange(newVal);
    onChangeType(helpers.creditCardTypeForNumber(newVal));
  };

  return (
    <TextField
      inputId={inputId}
      inputName={inputName}
      inputLabel={label}
      defaultValue={value as string}
      onChange={handleChange}
      maskOptions={{
        creditCard: true,
      }}
      inputError={error}
      helperText={helperText}
      inputErrorMessage={errorText}
      isDisabled={isDisabled}
      customDecoration={creditCardIcon}
    />
  );
}

export function makeExpitarionCardField({
  inputName,
  label,
  getInputProps,
  inputId,
  isDisabled,
  showError,
  isLoading,
}: FormFieldProps): React.ReactNode {
  const { value, onChange, error, errorText } = getInputProps(inputName);
  return (
    <TextField
      inputId={inputId}
      inputName={inputName}
      data-testid={inputName}
      inputLabel={label}
      defaultValue={value as string}
      onChange={onChange}
      isDisabled={isDisabled}
      loading={isLoading}
      inputError={showError || error}
      inputErrorMessage={errorText}
      maskOptions={{
        date: true,
        datePattern: ['m', 'y'],
      }}
    />
  );
}
